// Frame 09 — app shell, empty (33:2743): a 560px `State/Empty` panel centred in
// the body, under the same page header the loaded frame carries.
//
// TWO STATES, NOT ONE. `GET /docs` was deliberately given the vocabulary to
// tell them apart (US-053), and collapsing them is the defect:
//
//   no-documents   total: 0            -> "add your first document"
//   status-unknown total > 0, every    -> the documents exist; nobody can say
//                  row `unavailable`      whether their glossaries extracted,
//                                         because no model key is configured
//
// Neither is ever reached from a swallowed fetch error — a failed `GET /docs`
// renders an ErrorState in `OverviewScreen`, never this.

import { Button, EmptyState } from "../../ui";
import { hrefs } from "../../shell/hrefs";
import type { DocsVerdict } from "./api";
import "./overview.css";

export type OverviewEmptyProps = {
  kind: Exclude<DocsVerdict, "has-documents">;
  orgSlug: string;
  docCount: number;
  /** `GET /dashboards/{org}/sufficiency`'s `reasons[]` — one line per count
   *  that fell short. The thresholds live only in the backend, so this screen
   *  quotes them rather than holding its own copy. */
  reasons: string[];
};

export function OverviewEmpty({
  kind,
  orgSlug,
  docCount,
  reasons,
}: OverviewEmptyProps) {
  const to = hrefs(orgSlug);

  const noDocuments = kind === "no-documents";
  const headline = noDocuments
    ? "No documents yet"
    : "Extraction status is unavailable";
  const body = noDocuments
    ? "mAiSight builds a glossary from the documents you publish, then ranks the terms your readers stall on. Add the first document and this page fills in as readers arrive."
    : `${docCount} documents are stored, but no model key is configured, so mAiSight cannot tell whether their glossaries have been extracted. This is not an empty workspace — add a key and the numbers return.`;

  return (
    <div className="maisight-overview-panel" data-empty-kind={kind}>
      <EmptyState
        headline={headline}
        body={
          <>
            {body}
            {noDocuments && reasons.length > 0 ? (
              <span className="maisight-overview-reasons">
                {`Reader analytics need: ${reasons.join(" · ")}`}
              </span>
            ) : null}
          </>
        }
        action={
          <Button
            to={noDocuments ? to.install : to.settings}
            variant="primary"
          >
            {noDocuments ? "Add your first document" : "Configure a model key"}
          </Button>
        }
      />
    </div>
  );
}
